import { createContext, useContext, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { logoutFn, validateToken } from "src/api";
import { getFromLocalStorage, removeFromLocalStorage, setToLocalStorage, showSuccessToast } from "src/lib/utils";
import type { AuthSession, AuthUser } from "src/types/auth";

/** Keys cleared on logout and on a 401 — see `onResRejected` in `src/api/helpers`. */
const SESSION_KEYS = ["access_token", "user", "selectedBusiness", "selectedChild"];

const AuthContext = createContext<AuthSession>({
	user: null,
	logout: () => {},
	setUser: () => {},
	isAuthenticated: false,
});

export const AuthProvider = () => {
	const [user, setUser] = useState<AuthUser | null>(() => getFromLocalStorage<AuthUser>("user") ?? null);

	useEffect(() => {
		setToLocalStorage("user", user);
	}, [user]);

	const { mutateAsync } = useMutation(logoutFn, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "Logged out successfully");
		},
	});

	const logout = async () => {
		await mutateAsync();
		setUser(null);
		removeFromLocalStorage(...SESSION_KEYS);
	};

	// Revalidates the token every 5 minutes and refreshes the cached user with what comes back.
	const { data } = useQuery({
		queryKey: ["user"],
		queryFn: () => validateToken(),
		enabled: !!user,
		refetchInterval: 5 * 60 * 1000,
	});

	useEffect(() => {
		const validatedUser = data?.data?.user;

		if (validatedUser) setUser(validatedUser);
	}, [data]);

	return (
		<AuthContext.Provider value={{ isAuthenticated: !!user, logout, user, setUser }}>
			<Outlet />
		</AuthContext.Provider>
	);
};

export const useAuth = () => useContext(AuthContext);
